import { Button, Link } from '@mui/material'

import { UpdateMetadataButton } from './update-metadata-button.component'
import { useArweaveMetadata, useMe } from 'src/hooks'
import { EAccountRoles, IAccountByIdRespDto, IProductByIdRespDto } from 'src/types'
import {
  extractMetadataUriHash,
  getArweaveTxUrl,
  getArweaveUrl,
  getOpenseaTokenUrl,
  getTableTimestamp,
} from 'src/common/utils'
import {
  NFTMetadataSection,
  NFTOutlinedButton,
  NFTTextField,
} from 'src/components/nft-metadata-section'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { ProductScanExplorerButton } from '../fields'
import { LinkIcon } from 'src/assets/icons'

interface IProps {
  isLoading: boolean
  account?: IAccountByIdRespDto
  isSuperadmin: boolean
  product?: IProductByIdRespDto
  locale?: string
}

export const ProductNFTMetadataSection = ({
  isLoading,
  account,
  isSuperadmin,
  product,
  locale,
}: IProps) => {
  const { data: me } = useMe()

  const arweaveUrl = product?.arweave_url
  const data = useArweaveMetadata(arweaveUrl || null)

  if (!arweaveUrl || !product.token_id) {
    return null
  }

  const metadataChanged = data && product.name !== data?.name
  const isAccountOwner = isSuperadmin || account?.currentRoles.includes(EAccountRoles.owner)

  const isOwner = account?.currentRoles.includes(EAccountRoles.owner) || isSuperadmin

  const showLoaders = (isLoading && !data) || !data
  return (
    <NFTMetadataSection
      loadersNumber={3}
      isLoading={showLoaders}
      label={
        <HelperWrapper
          t="account-form-nft-metadata-label"
          isColorInherited
          size={11}
          verticalPosition={15}
        />
      }
      image={product.image_nft_medium || getArweaveUrl(extractMetadataUriHash(data?.image || ''))}
      fields={
        <>
          <NFTTextField
            label={
              <HelperWrapper
                t="products-form-name-input-label"
                isColorInherited
                size={11}
                verticalPosition={15}
              />
            }
            value={data?.name}
          />
          <NFTTextField
            label={
              <HelperWrapper
                t="creation-date-label"
                isColorInherited
                size={11}
                verticalPosition={15}
              />
            }
            value={getTableTimestamp({
              date: new Date(Number(data?.attributes?.[0]?.value) * 1000),
              timezone: me?.timezone,
            })}
          />
        </>
      }
      actions={
        <>
          <UpdateMetadataButton
            locale={locale}
            disabled={isLoading || !metadataChanged || !isOwner || product.is_minting}
          />
          {isSuperadmin && (
            <NFTOutlinedButton
              arweaveUrl={arweaveUrl}
              text={
                <HelperWrapper
                  t="arweave-metadata-button-text"
                  isColorInherited
                  verticalPosition={38}
                />
              }
            />
          )}
          {isSuperadmin && (
            <NFTOutlinedButton
              isDisabled={process.env.REACT_APP_STATUS_ENV !== 'prod'}
              text={
                <HelperWrapper
                  t="arweave-metadata-button-text-1"
                  isColorInherited
                  verticalPosition={38}
                />
              }
              arweaveUrl={getArweaveTxUrl(extractMetadataUriHash(arweaveUrl))}
            />
          )}
          <NFTOutlinedButton
            text={
              <HelperWrapper
                t="arweave-metadata-button-text-2"
                isColorInherited
                verticalPosition={38}
              />
            }
            arweaveUrl={getOpenseaTokenUrl(
              process.env.REACT_APP_PRODUCT_CONTRACT_HASH!,
              product.token_id,
            )}
          />
          <div>
            <Button
              // size="small"
              component={Link}
              target="_blank"
              href={
                getArweaveUrl(extractMetadataUriHash(data?.image || '')) ||
                product.image_nft_medium!
              }
              variant="outlined"
              endIcon={<LinkIcon />}
            >
              <HelperWrapper t="open-image-button-text" isColorInherited verticalPosition={38} />
            </Button>
          </div>
          {product && (
            <ProductScanExplorerButton
              locale={locale}
              tokenId={product?.token_id}
              isMinting={product?.is_minting}
              arweaveUri={product?.arweave_url}
              isAccountOwner={!!isAccountOwner}
            />
          )}
        </>
      }
    />
  )
}
